/**
 * embedded — pure helpers for detecting whether the client is running inside a
 * Discord Activity iframe, and for prefixing network paths through the Discord
 * proxy when it is. No @discord/embedded-app-sdk dependency yet — these are the
 * two facts every network call needs, kept dependency-free so the whole app
 * (driver, audio) can import them without pulling in the SDK.
 *
 * Discord Activities run inside an iframe served from `<app>.discordsays.com`
 * and reach the developer's backend ONLY through a same-origin `/.proxy` prefix
 * (arbitrary external hosts are blocked by CSP). So when embedded, every request
 * to our own server — the ws upgrade and the `/api/*` fetches — must go through
 * `/.proxy`; outside Discord the prefix is empty and behavior is unchanged.
 */

/**
 * True when the page is running inside a Discord Activity iframe. Detected by
 * the `.discordsays.com` proxy host OR the `frame_id` launch query parameter
 * Discord injects into the Activity URL. Safe on the server (returns false).
 */
export function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host.endsWith('.discordsays.com')) return true;
  const params = new URLSearchParams(window.location.search);
  return params.has('frame_id');
}

/**
 * Network path prefix for reaching our own backend. `'/.proxy'` inside a Discord
 * Activity (so `/.proxy/ws`, `/.proxy/api/audio/:id`), `''` everywhere else. Use
 * it to build every URL that targets the game server.
 */
export function netBase(): string {
  return isEmbedded() ? '/.proxy' : '';
}
